import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getWorkspaceItems } from '../../services/api';
import { AGREEMENT_REVIEW_COUNT } from '../../data/mockAgreements';
import { useTheme } from '../../context/ThemeContext';
import logo from '../../assets/logo.svg';

// --- Navigation data structure ---
const NAV_STRUCTURE = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    type: 'top-level',
    pageKey: 'dashboard',
    icon: 'dashboard',
  },
  {
    id: 'finance',
    label: 'Finance & Accounting',
    type: 'group',
    icon: 'finance',
    children: [
      { pageKey: 'forecast-dashboard', label: 'Forecast Dashboard' },
      { pageKey: 'forecast-tool', label: 'Forecast Tool', comingSoon: true },
      { pageKey: 'reports-analysis', label: 'Reports & Analysis', comingSoon: true },
    ],
  },
  {
    id: 'growth',
    label: 'Growth',
    type: 'group',
    icon: 'growth',
    children: [
      { pageKey: 'growth-dashboard', label: 'Growth Dashboard' },
      { pageKey: 'nda-ta-generator', label: 'NDA/TA Generator' },
      { pageKey: 'agreement-review', label: 'Agreement Review' },
      { pageKey: 'growth-tools', label: 'Growth Tools', externalUrl: process.env.REACT_APP_GROWTH_TOOLS_URL || '/growth-tools/index.html', requiredGroup: 'growth' },
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    type: 'group',
    icon: 'delivery',
    children: [
      { pageKey: 'delivery-dashboard', label: 'Utilization Dashboard' },
      { pageKey: 'delivery-config', label: 'Reference Data' },
      { pageKey: 'profitability-reports', label: 'Profitability Reports', comingSoon: true },
    ],
  },
  {
    id: 'contracts-manager',
    label: 'Contracts',
    type: 'group',
    icon: 'contracts',
    children: [
      { pageKey: 'contracts', label: 'Funding Dashboard' },
      { pageKey: 'companies', label: 'Customers' },
      { pageKey: 'workspace', label: 'Workspace', comingSoon: true },
    ],
  },
  {
    id: 'security',
    label: 'Security & Facilities',
    type: 'group',
    icon: 'security',
    children: [
      { pageKey: 'security-dashboard', label: 'Dashboard', comingSoon: true },
      { pageKey: 'visit-requests', label: 'Visit Requests' },
      { pageKey: 'clearance-requests', label: 'Clearance Tracking' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    type: 'top-level',
    pageKey: 'insights',
    icon: 'insights',
  },
];

// Reverse lookup: pageKey -> parent group id
const PAGE_TO_GROUP = {};
NAV_STRUCTURE.forEach((item) => {
  if (item.type === 'group' && item.children) {
    item.children.forEach((child) => {
      PAGE_TO_GROUP[child.pageKey] = item.id;
    });
  }
});

// --- Icon components ---
const NavIcon = ({ name }) => {
  const icons = {
    dashboard: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
    contracts: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
    ),
    finance: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    delivery: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    growth: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
    security: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    insights: (
      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
  };
  return icons[name] || null;
};

const ChevronIcon = ({ expanded }) => (
  <svg
    className={`nav-chevron ${expanded ? 'expanded' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const APP_VERSION = process.env.REACT_APP_COMMIT_HASH
  ? process.env.REACT_APP_COMMIT_HASH.substring(0, 7)
  : 'local';
const IS_DEV = (process.env.REACT_APP_SITE_DOMAIN || '').includes('bcorex.ninja') || process.env.NODE_ENV === 'development';

// --- Sidebar component ---
function Sidebar({ activePage, onNavigate, user, onLogout }) {
  const { theme, toggleTheme } = useTheme();
  const userEmail = user ? user.email : '';
  const isAdminUser = user?.groups?.includes('admin') ?? false;
  const isFinanceUser = user?.groups?.includes('finance') ?? false;
  const isGrowthUser = user?.groups?.includes('growth') ?? false;

  const getInitials = (email) => {
    if (!email) return '??';
    const name = email.split('@')[0];
    const parts = name.split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(userEmail);

  // Fetch pending review count for workspace badge
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetchReviewCount() {
      try {
        const items = await getWorkspaceItems('PENDING_REVIEW');
        if (!cancelled) setReviewCount(Array.isArray(items) ? items.length : 0);
      } catch {
        // Silently ignore — badge just won't show
      }
    }
    fetchReviewCount();
    const interval = setInterval(fetchReviewCount, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = new Set();
    const parentGroup = PAGE_TO_GROUP[activePage];
    if (parentGroup) {
      initial.add(parentGroup);
    }
    return initial;
  });

  // Auto-expand only the group containing the active page
  useEffect(() => {
    const parentGroup = PAGE_TO_GROUP[activePage];
    if (parentGroup) {
      setExpandedGroups(new Set([parentGroup]));
    }
  }, [activePage]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      if (prev.has(groupId)) {
        return new Set();
      }
      return new Set([groupId]);
    });
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src={logo} alt="B/CORE" className="sidebar-logo-img" />
        <span>Operations Portal</span>
        <span className="sidebar-version">
          v. {APP_VERSION}
          {IS_DEV && <span className="sidebar-env-badge">DEV</span>}
        </span>
      </div>

      <div className="sidebar-section">
        <ul className="sidebar-nav">
          {NAV_STRUCTURE.map((item) => {
            if (item.type === 'top-level') {
              return (
                <li key={item.id}>
                  <button
                    className={activePage === item.pageKey ? 'active' : ''}
                    onClick={() => onNavigate(item.pageKey)}
                  >
                    <NavIcon name={item.icon} />
                    {item.label}
                  </button>
                </li>
              );
            }

            if (item.type === 'group') {
              const isExpanded = expandedGroups.has(item.id);
              const hasActiveChild = item.children.some(
                (child) => child.pageKey === activePage
              );

              return (
                <li key={item.id} className="nav-group">
                  <button
                    className={`nav-group-header ${isExpanded ? 'expanded' : ''} ${hasActiveChild ? 'group-active' : ''}`}
                    onClick={() => toggleGroup(item.id)}
                  >
                    <NavIcon name={item.icon} />
                    {item.label}
                    <ChevronIcon expanded={isExpanded} />
                  </button>
                  {isExpanded && (
                    <ul className="nav-group-items">
                      {item.children
                        .filter((child) => !child.requiredGroup || user?.groups?.includes(child.requiredGroup) || user?.groups?.includes('admin'))
                        .map((child) => (
                        <li key={child.pageKey}>
                          {child.externalUrl ? (
                            <a
                              href={child.externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="nav-external-link"
                            >
                              {child.label}
                              <svg className="external-link-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </a>
                          ) : (
                            <button
                              className={`${activePage === child.pageKey ? 'active' : ''} ${child.comingSoon ? 'coming-soon' : ''}`}
                              onClick={() => onNavigate(child.pageKey)}
                            >
                              {child.label}
                              {child.pageKey === 'workspace' && reviewCount > 0 && <span className="badge">{reviewCount}</span>}
                              {child.pageKey === 'agreement-review' && AGREEMENT_REVIEW_COUNT > 0 && <span className="badge">{AGREEMENT_REVIEW_COUNT}</span>}
                              {child.comingSoon && <span className="coming-soon-tag">Soon</span>}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            return null;
          })}
        </ul>
      </div>

      {(isAdminUser || isFinanceUser || isGrowthUser) && (
        <div className="sidebar-section">
          <div className="sidebar-section-label">Data Import</div>
          <ul className="sidebar-nav">
            {(isAdminUser || isFinanceUser) && (
              <li>
                <button
                  className={activePage === 'finance-import' ? 'active' : ''}
                  data-page="finance-import"
                  onClick={() => onNavigate('finance-import')}
                >
                  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                  Finance Import
                </button>
              </li>
            )}
            <li>
              <button
                className={activePage === 'growth-import' ? 'active' : ''}
                data-page="growth-import"
                onClick={() => onNavigate('growth-import')}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                Growth Import
              </button>
            </li>
            <li>
              <button
                className={activePage === 'ute-reports' ? 'active' : ''}
                data-page="ute-reports"
                onClick={() => onNavigate('ute-reports')}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                UTE Import
              </button>
            </li>
          </ul>
        </div>
      )}

      {isAdminUser && (
        <div className="sidebar-section">
          <div className="sidebar-section-label">Admin</div>
          <ul className="sidebar-nav">
            <li>
              <button
                className={activePage === 'users' ? 'active' : ''}
                data-page="users"
                onClick={() => onNavigate('users')}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Users
              </button>
            </li>
            <li>
              <button
                className={activePage === 'settings' ? 'active' : ''}
                data-page="settings"
                onClick={() => onNavigate('settings')}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Settings
              </button>
            </li>
          </ul>
        </div>
      )}

      <div className="theme-toggle">
        <svg className="theme-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {theme === 'dark' ? (
            <>
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </>
          ) : (
            <>
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </>
          )}
        </svg>
        <span className="theme-toggle-label">
          {theme === 'dark' ? 'Dark' : 'Light'} Mode
        </span>
        <button
          className={`theme-toggle-switch ${theme === 'light' ? 'active' : ''}`}
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          <span className="theme-toggle-knob" />
        </button>
      </div>

      <div className="sidebar-user">
        <div className="sidebar-user-avatar">{initials}</div>
        <div className="sidebar-user-info">
          <div className="sidebar-user-name">{userEmail}</div>
          <div className="sidebar-user-role">{isAdminUser ? 'Admin' : 'User'}</div>
          <button
            className="sidebar-logout-btn"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </aside>
  );
}

NavIcon.propTypes = {
  name: PropTypes.string.isRequired,
};

ChevronIcon.propTypes = {
  expanded: PropTypes.bool.isRequired,
};

Sidebar.propTypes = {
  activePage: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }),
  onLogout: PropTypes.func,
};

export default Sidebar;
